'use client';

import { useTranslations } from 'next-intl';
import { DocsLinks } from '@/components/how-it-works/docs-links';
import {
  Coins01Icon,
  SparklesIcon,
  Timer01Icon,
  UserMultipleIcon,
} from '@/components/ui/icon/glyphs';
import { InfoModal } from '@/components/ui/info-modal';

interface WhatIsAProposalModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Explains what a proposal is, shown from the `?` button in the proposal detail
 * header and from the "What is a proposal?" cross-link inside the Launchpad
 * how-it-works modal. Scoped to a single proposal — a suggested market that
 * isn't live yet. Backers rally demand and earn a share of the future market's
 * trading fees for backing early.
 */
export function WhatIsAProposalModal({
  open,
  onOpenChange,
}: WhatIsAProposalModalProps): React.ReactNode {
  const t = useTranslations('what_is_a_proposal');

  return (
    <InfoModal
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      title={t('title')}
      description={t('description')}
      footer={<InfoModal.Footer cta={t('got_it')} onClose={(): void => onOpenChange(false)} />}
    >
      <InfoModal.IconList>
        <InfoModal.IconItem
          icon={Timer01Icon}
          title={t('what_title')}
          description={t('what_description')}
        />
        <InfoModal.IconItem
          icon={UserMultipleIcon}
          title={t('rally_title')}
          description={t('rally_description')}
        />
        <InfoModal.IconItem
          icon={Coins01Icon}
          title={t('prize_pool_title')}
          description={t('prize_pool_description')}
        />
        <InfoModal.IconItem
          icon={SparklesIcon}
          title={t('earn_title')}
          description={t('earn_description')}
        />
      </InfoModal.IconList>
      <InfoModal.Callout>{t('info')}</InfoModal.Callout>
      <DocsLinks docsPath="/guides/launchpad" />
    </InfoModal>
  );
}
